import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { queryKeys } from "django/queryKeys";
import { ApplicationUser } from "graphql/_Types";
import { getEnvVar } from "ideas.env";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserPersonalSpaceRequestStatus } from "types/constants";
import { getRequestHeaders } from "utils/getRequestHeaders";

type PollPersonalSpaceValue = {
  hasPersonalSpace: boolean;
  status: UserPersonalSpaceRequestStatus | null;
  isOnboarded: boolean;
  startPolling: () => void;
};

const listPersonalSpaceRequestDjango = async () => {
  const headers = await getRequestHeaders();
  const response = await axios.get<
    {
      id: number;
      status: UserPersonalSpaceRequestStatus;
      user: ApplicationUser["id"];
    }[]
  >(getEnvVar("URL_TENANT_USERS_PERSONAL_SPACE"), { headers });
  return response.data;
};

export const usePollPersonalSpace = () => {
  const hasPersonalSpace = useUserContext((s) => s.personalSpace !== undefined);

  const refetchUserProviderQuery = useUserContext(
    (s) => s.refetchUserProviderQuery,
  );
  const [isLoading, setIsLoading] = useState(!hasPersonalSpace);
  const [status, setStatus] = useState<
    PollPersonalSpaceValue["status"] | undefined
  >(hasPersonalSpace ? UserPersonalSpaceRequestStatus.COMPLETED : undefined);
  const [requestId, setRequestId] = useState<number | null>(null);
  /**
   * Track initial status so we can determine whether a user
   * refetch is necessary when completed status is reached
   */
  const initialStatus = useRef<PollPersonalSpaceValue["status"] | undefined>();

  const [poll, setPoll] = useState(false);

  const { data, refetch } = useQuery({
    queryFn: listPersonalSpaceRequestDjango,
    queryKey: queryKeys.personalSpace,
    // 10 seconds
    refetchInterval: poll ? 1_000 * 10 : undefined,
    retry: 3,
    refetchOnMount: "always",
    enabled: !hasPersonalSpace,
  });

  const startPolling = useCallback(() => {
    setPoll(true);
    void refetch({ throwOnError: false });
  }, [refetch]);

  useEffect(() => {
    if (data !== undefined) {
      const status = data[0]?.status ?? null;
      const requestId = data[0]?.id ?? null;
      setRequestId(requestId);
      setStatus(status);
      setIsLoading(false);
      if (initialStatus.current === undefined) {
        initialStatus.current = status;
      }

      /**
       * Auto poll if status is pending
       */
      if (status === UserPersonalSpaceRequestStatus.PENDING) {
        setPoll(true);
      }

      /**
       * Stop polling in terminal states
       */
      if (
        status === UserPersonalSpaceRequestStatus.COMPLETED ||
        status === UserPersonalSpaceRequestStatus.FAILED
      ) {
        setPoll(false);
      }

      /**
       * Refetch User Provider Query when space creation succeeds
       */

      if (
        initialStatus.current !== UserPersonalSpaceRequestStatus.COMPLETED &&
        status === UserPersonalSpaceRequestStatus.COMPLETED
      ) {
        void refetchUserProviderQuery();
      }
    }
  }, [data, refetchUserProviderQuery]);

  const value = useMemo(
    () => ({
      status,
      requestId,
      startPolling,
      isLoading,
    }),
    [startPolling, status, requestId, isLoading],
  );

  return value;
};
